import React from 'react';
import { Box, Drawer, IconButton, Stack, Typography } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { CloseOutlined } from '@ant-design/icons';
import { CheckoutProvider } from './CheckoutProvider';
import { CheckoutRouter } from './CheckoutRouter';
import { CHECKOUT_COPY, CHECKOUT_STEPS } from 'lib/billing';
import { useBillingClient } from 'client/client-hook';
import useAuth from 'hooks/useAuth';
import { useBillingSync, useCheckoutState, useCheckoutStore } from './store';
import { useInvoicePreviewStore } from './store/useInvoicePreviewStore';

type InitialSelection = Partial<Record<string, any>>;

type Props = {
  open: boolean;
  onClose: () => void;
  onComplete?: (payload: any) => void;
  initialSelection?: InitialSelection;
};

const DRAWER_WIDTHS = { xs: '100%', sm: '500px', md: '540px' };

const DRAWER_SX = { zIndex: (t: Theme) => t.zIndex.drawer + 1000 };

const DRAWER_PAPER_PROPS = {
  sx: {
    width: DRAWER_WIDTHS,
    maxWidth: '100vw',
    bgcolor: 'background.paper',
    backgroundImage: 'none',
    boxShadow: 'none',
    borderTopLeftRadius: 16,
    borderBottomLeftRadius: 16,
    overflow: 'hidden'
  }
} as const;

const DRAWER_HEADER_SX = { px: 3, py: 2, borderBottom: 1, borderColor: 'divider', flexShrink: 0 };

const DRAWER_BODY_SX = { flex: 1, overflowY: 'auto', px: 3, py: 3 };

export default function CheckoutDrawer({ open, onClose, onComplete, initialSelection }: Props) {
  const { user, isAuthenticated } = useAuth() as { user?: { tcId?: string }; isAuthenticated?: boolean };
  const getBillingClient = useBillingClient();

  useBillingSync({
    enabled: Boolean(isAuthenticated && user?.tcId),
    userId: user?.tcId ?? null,
    clientFactory: getBillingClient
  });

  React.useEffect(() => {
    if (!open) return;
    // (Re)enter at the Configure step unless this open explicitly requested a
    // step (openDrawer(step) — e.g. DirectBuyMcpModal jumps straight to Payment
    // after configuring in-modal). The drawer is keepMounted, so the store's
    // `step` survives across open/close — without this reset, reopening after a
    // completed payment would land back on the Payment step showing stale
    // current-subscription values instead of the editable plan. requestedStep
    // is one-shot per open: every openDrawer() call rewrites it, and close
    // clears it. NOT cleared here — when the drawer mounts fresh with the
    // store already open (quick-subscribe from the welcome tour lands on the
    // dashboard mid-checkout), StrictMode runs this effect twice, and an
    // immediate clear made the second pass reset a requested Payment entry
    // back to Configure. Reading it idempotently survives the double-run.
    const requested = useCheckoutStore.getState().requestedStep;
    useCheckoutStore.getState().setStep((requested ?? CHECKOUT_STEPS.configure) as any);
    // Coupon resets with the drawer like step/preview — a code typed during a
    // previous open must never silently re-apply to a fresh cart.
    useCheckoutStore.getState().setCoupon('');
    useInvoicePreviewStore.getState().reset();
    if (initialSelection) {
      useCheckoutStore.getState().setSelection(initialSelection as any);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const state = useCheckoutState();
  const drawerTitle = state.isModifyingExisting ? 'Update subscription' : 'New subscription';

  return (
    <Drawer anchor="right" open={open} onClose={onClose} keepMounted sx={DRAWER_SX} PaperProps={DRAWER_PAPER_PROPS}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={DRAWER_HEADER_SX}>
        <Typography variant="h5" fontWeight={600}>
          {drawerTitle}
        </Typography>
        <IconButton onClick={onClose} size="small" aria-label={CHECKOUT_COPY.drawer.closeAria}>
          <CloseOutlined />
        </IconButton>
      </Stack>
      <Box sx={DRAWER_BODY_SX}>
        <CheckoutProvider onComplete={onComplete}>
          <CheckoutRouter />
        </CheckoutProvider>
      </Box>
    </Drawer>
  );
}
